import React from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { gbp } from "../../lib/apiClient";
import { Section } from "./layout";
import { useRowDrag } from "./useRowDrag";
import { numVal, parseNum } from "./utils";

/**
 * MoneyLineTable — generic description + cost (+ optional markup) table.
 * Used for Callouts (no markup) and Extras (with markup).
 *
 * When `withMarkup` is true the table renders extra columns for Markup % and
 * Markup £ and the row total includes the marked-up sell figure. The default
 * is 35 % to match Parts & Materials.
 */
export function MoneyLineTable({
  title,
  subtitle,
  testIdPrefix,
  items,
  onChange,
  placeholder,
  tmpId,
  totalLabel,
  previewTotal,
  storageKey,
  withMarkup = false,
  defaultMarkup = 35,
}) {
  const drag = useRowDrag(items || [], onChange, testIdPrefix);
  const add = () => onChange([
    ...(items || []),
    withMarkup
      ? { id: tmpId(), description: "", cost: 0, markup_percent: defaultMarkup }
      : { id: tmpId(), description: "", cost: 0 },
  ]);
  const setRow = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  const headers = withMarkup
    ? ["Description", "Cost £", "Markup %", "Markup £", "Line Total", ""]
    : ["Description", "Cost £", ""];
  const colCount = headers.length + 1; // +1 for drag handle col

  return (
    <Section
      title={title}
      subtitle={subtitle}
      collapsible
      storageKey={storageKey}
      action={
        <button
          onClick={add}
          className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 hover:text-slate-900 text-slate-600"
          data-testid={`add-${testIdPrefix}-button`}
        >
          <Plus size={14} /> Add line
        </button>
      }
    >
      <div>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="w-full text-sm table-fixed min-w-[640px]">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th style={{ width: 28 }} className="px-1 py-2"></th>
              {headers.map((h, i) => (
                <th
                  key={h || `col-${i}`}
                  className={`px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 ${i >= 1 && i <= headers.length - 2 ? "text-right" : "text-left"}`}
                >{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(items || []).length === 0 && (
              <tr><td colSpan={colCount} className="px-3 py-6 text-center text-slate-400 text-sm">No {title.toLowerCase()} yet.</td></tr>
            )}
            {(items || []).map((ln, idx) => {
              const cost = Number(ln.cost || 0);
              const mp = Number(ln.markup_percent || 0);
              const markupVal = cost * mp / 100;
              const lineTotal = cost * (1 + mp / 100);
              return (
                <tr key={ln.id || idx} className="border-b border-slate-200" {...drag.dragProps(idx)}>
                  <td className="px-1 py-1 align-middle text-slate-400 hover:text-slate-700" {...drag.handleProps(idx)}>
                    <GripVertical size={14} />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={ln.description}
                      onChange={(e) => setRow(idx, { description: e.target.value })}
                      className="qb-input h-9"
                      data-testid={`${testIdPrefix}-desc-${idx}`}
                      placeholder={placeholder}
                    />
                  </td>
                  <td className="px-1 py-1 w-32">
                    <input
                      type="number" step="0.01"
                      value={numVal(ln.cost)}
                      onChange={(e) => setRow(idx, { cost: parseNum(e.target.value) })}
                      className="qb-input h-9 font-mono text-right"
                      data-testid={`${testIdPrefix}-cost-${idx}`}
                    />
                  </td>
                  {withMarkup && (
                    <>
                      <td className="px-1 py-1 w-24">
                        <input
                          type="number" step="0.1" min={withMarkup ? "35" : "0"}
                          value={numVal(ln.markup_percent)}
                          onChange={(e) => {
                            // Per Feb 2026 spec, Extras markup floor is 35%.
                            // Clamp on change so the value can't be persisted lower.
                            const v = parseNum(e.target.value);
                            setRow(idx, { markup_percent: Math.max(v, 35) });
                          }}
                          className="qb-input h-9 font-mono text-right"
                          data-testid={`${testIdPrefix}-markup-${idx}`}
                          title="Extras markup has a 35% floor. Increase as needed."
                        />
                      </td>
                      <td className="px-2 py-1 font-mono text-right whitespace-nowrap text-slate-500" data-testid={`${testIdPrefix}-markup-value-${idx}`}>{gbp(markupVal)}</td>
                      <td className="px-2 py-1 font-mono text-right whitespace-nowrap font-semibold" data-testid={`${testIdPrefix}-line-total-${idx}`}>{gbp(lineTotal)}</td>
                    </>
                  )}
                  <td className="px-2 py-1 text-right">
                    <button
                      onClick={() => removeRow(idx)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`remove-${testIdPrefix}-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      {(items || []).length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm bg-slate-50 rounded-lg px-4 py-2">
          <span className="text-slate-600">{totalLabel}</span>
          <span className="font-mono font-semibold text-brand-blue" data-testid={`${testIdPrefix}-total`}>{gbp(previewTotal || 0)}</span>
        </div>
      )}
    </Section>
  );
}
